const FALLBACK_GLOW = '220, 38, 38';
const STORAGE_KEY_PREFIX = 'game-app-tracker:cover-glow:';
const COLOR_SAMPLE_SIZE = 8;

const pendingGlowBySource = new Map();
const hydratedCards = new WeakSet();

export function initCoverReactiveCardGlow() {
    if (!(document.body instanceof HTMLElement)) {
        return;
    }

    hydrateCards(document.body.querySelectorAll('.ui-game-card'));
    observeCardInsertions(document.body);
}

function observeCardInsertions(rootElement) {
    const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
            mutation.addedNodes.forEach((node) => {
                if (!(node instanceof HTMLElement)) {
                    return;
                }

                if (node.matches('.ui-game-card')) {
                    hydrateCard(node);
                }

                const nestedCards = node.querySelectorAll('.ui-game-card');
                if (nestedCards.length > 0) {
                    hydrateCards(nestedCards);
                }
            });
        });
    });

    observer.observe(rootElement, {
        childList: true,
        subtree: true,
    });
}

function hydrateCards(cards) {
    cards.forEach((card) => {
        hydrateCard(card);
    });
}

function hydrateCard(card) {
    if (!(card instanceof HTMLElement) || hydratedCards.has(card)) {
        return;
    }

    hydratedCards.add(card);

    if (card.style.getPropertyValue('--card-glow').trim() !== '') {
        return;
    }

    const image = card.querySelector('.ui-card-cover img');
    if (!(image instanceof HTMLImageElement)) {
        return;
    }

    const applyGlow = (rgbTriplet) => {
        card.style.setProperty('--card-glow', normalizeGlowTriplet(rgbTriplet));
    };

    const applyImageGlow = () => {
        resolveGlowForImage(image)
            .then(applyGlow)
            .catch(() => applyGlow(FALLBACK_GLOW));
    };

    if (image.complete) {
        if (image.naturalWidth > 0 && image.naturalHeight > 0) {
            applyImageGlow();

            return;
        }

        applyGlow(FALLBACK_GLOW);

        return;
    }

    image.addEventListener('load', applyImageGlow, { once: true });
    image.addEventListener('error', () => applyGlow(FALLBACK_GLOW), { once: true });
}

function resolveGlowForImage(image) {
    const source = normalizeSourceUrl(image.currentSrc || image.src);
    if (source === '') {
        return Promise.resolve(FALLBACK_GLOW);
    }

    const cachedGlow = readCachedGlow(source);
    if (cachedGlow !== null) {
        return Promise.resolve(cachedGlow);
    }

    const pending = pendingGlowBySource.get(source);
    if (pending !== undefined) {
        return pending;
    }

    const pendingGlow = Promise.resolve()
        .then(() => extractGlowFromImage(image))
        .catch(() => FALLBACK_GLOW)
        .then((rgbTriplet) => {
            const normalized = normalizeGlowTriplet(rgbTriplet);
            cacheGlow(source, normalized);

            return normalized;
        })
        .finally(() => {
            pendingGlowBySource.delete(source);
        });

    pendingGlowBySource.set(source, pendingGlow);

    return pendingGlow;
}

function extractGlowFromImage(image) {
    const canvas = document.createElement('canvas');
    canvas.width = COLOR_SAMPLE_SIZE;
    canvas.height = COLOR_SAMPLE_SIZE;

    const context = canvas.getContext('2d', { willReadFrequently: true });
    if (context === null) {
        return FALLBACK_GLOW;
    }

    context.drawImage(image, 0, 0, COLOR_SAMPLE_SIZE, COLOR_SAMPLE_SIZE);
    const pixelData = context.getImageData(0, 0, COLOR_SAMPLE_SIZE, COLOR_SAMPLE_SIZE).data;

    let redTotal = 0;
    let greenTotal = 0;
    let blueTotal = 0;
    let pixelCount = 0;

    for (let index = 0; index < pixelData.length; index += 4) {
        const red = pixelData[index];
        const green = pixelData[index + 1];
        const blue = pixelData[index + 2];
        const alpha = pixelData[index + 3];

        if (alpha < 64) {
            continue;
        }

        const brightness = (red + green + blue) / 3;
        if (brightness <= 30 || brightness >= 220) {
            continue;
        }

        redTotal += red;
        greenTotal += green;
        blueTotal += blue;
        pixelCount += 1;
    }

    if (pixelCount === 0) {
        return FALLBACK_GLOW;
    }

    const dominantRed = Math.round(redTotal / pixelCount);
    const dominantGreen = Math.round(greenTotal / pixelCount);
    const dominantBlue = Math.round(blueTotal / pixelCount);

    return `${dominantRed}, ${dominantGreen}, ${dominantBlue}`;
}

function readCachedGlow(source) {
    try {
        const cached = window.localStorage.getItem(storageKey(source));
        if (cached === null) {
            return null;
        }

        return normalizeGlowTriplet(cached);
    } catch (_error) {
        return null;
    }
}

function cacheGlow(source, rgbTriplet) {
    try {
        window.localStorage.setItem(storageKey(source), normalizeGlowTriplet(rgbTriplet));
    } catch (_error) {
        // localStorage can be unavailable (privacy mode/quota); silently continue.
    }
}

function storageKey(source) {
    return `${STORAGE_KEY_PREFIX}${encodeURIComponent(source)}`;
}

function normalizeSourceUrl(value) {
    const source = String(value).trim();
    if (source === '') {
        return '';
    }

    return source;
}

function normalizeGlowTriplet(value) {
    const match = String(value).match(/^\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})\s*$/);
    if (match === null) {
        return FALLBACK_GLOW;
    }

    const [red, green, blue] = match.slice(1).map((channel) => Number.parseInt(channel, 10));
    const channels = [red, green, blue];

    if (channels.some((channel) => Number.isNaN(channel) || channel < 0 || channel > 255)) {
        return FALLBACK_GLOW;
    }

    return `${red}, ${green}, ${blue}`;
}
